import { useQuery } from '@tanstack/react-query';
import { User, MapPin, Search, Loader2, CheckCircle2 } from 'lucide-react';
import { api } from '@/api/client';

interface Address { id: string; label?: string; address1: string; address2?: string; city: string; state: string; pincode: string; isDefault?: boolean; }
interface Customer { id: string; name: string; mobile: string; altMobile?: string; email?: string; totalOrders: number; lastOrderAt?: string; addresses: Address[]; }

interface Props {
  merchantId?: number;
  mobile: string;
  onPick: (data: { customer: Customer; address: Address }) => void;
  className?: string;
}

/** Shows under the mobile field on Create Order. When 10-digit mobile matches a
 *  saved customer, render a card per address; click to autofill the rest of the form. */
export function CustomerLookupCard({ merchantId, mobile, onPick, className = '' }: Props) {
  const m = (mobile || '').replace(/\D/g, '').slice(-10);
  const enabled = m.length === 10 && !!merchantId;

  const { data, isLoading } = useQuery({
    queryKey: ['customer-lookup', merchantId, m],
    enabled,
    queryFn: async () => (await api.get(`/merchants/${merchantId}/customers/lookup`, { params: { mobile: m } })).data as { found: boolean; customers: Customer[] },
  });

  if (!enabled) return null;
  if (isLoading) return <div className={`text-xs text-slate-500 flex items-center gap-1 ${className}`}><Loader2 size={12} className="animate-spin" /> Looking up customer…</div>;
  if (!data?.found || data.customers.length === 0) return (
    <div className={`text-xs text-slate-400 flex items-center gap-1 ${className}`}>
      <Search size={12} /> New customer — details will be saved after submit
    </div>
  );

  // Empty-address fallback so the customer name still autofills even when the
  // legacy address rows haven't been backfilled yet.
  const EMPTY_ADDR: Address = { id: '__empty__', address1: '', city: '', state: '', pincode: '' };

  return (
    <div className={`space-y-2 ${className}`}>
      <div className="text-xs font-semibold text-emerald-700 flex items-center gap-1">
        <CheckCircle2 size={12} /> {data.customers.length} customer(s) found — click a card to autofill
      </div>
      <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
        {data.customers.flatMap(c =>
          (c.addresses.length === 0
            ? [{ c, a: null as Address | null }]
            : c.addresses.map(a => ({ c, a }))
          )
        ).map(({ c, a }, idx) => (
          <button
            key={`${c.id}-${a?.id ?? idx}`}
            type="button"
            onClick={() => onPick({ customer: c, address: a ?? EMPTY_ADDR })}
            className="text-left border border-slate-200 rounded-lg p-2 hover:border-indigo-300 hover:bg-indigo-50/40 transition">
            <div className="flex items-center gap-1.5 text-sm font-medium text-slate-900">
              <User size={12} className="text-indigo-600" /> {c.name}
            </div>
            <div className="text-[10px] text-slate-400 mb-1">{c.mobile} · {c.totalOrders} orders</div>
            {a ? (
              <div className="text-xs text-slate-600 flex items-start gap-1">
                <MapPin size={10} className="mt-0.5 shrink-0 text-slate-400" />
                <div>
                  {a.label && <span className="text-[10px] bg-slate-100 text-slate-600 px-1 rounded mr-1">{a.label}</span>}
                  {a.address1}{a.address2 ? ', ' + a.address2 : ''}, {a.city}, {a.state} - <span className="font-mono">{a.pincode}</span>
                </div>
              </div>
            ) : (
              <div className="text-[10px] italic text-slate-400 flex items-center gap-1">
                <MapPin size={10} /> No saved address — click to autofill name only
              </div>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
